import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CrmRow } from "../components/CrmRow.jsx";
import { CustomSelect } from "../components/CustomSelect.jsx";
import { FiltersModal } from "../components/FiltersModal.jsx";
import { FilterChips } from "../components/FilterChips.jsx";
import { LoadingBlock } from "../components/LoadingBlock.jsx";
import { BottomSheetModal } from "../components/BottomSheetModal.jsx";
import { useMe } from "../../hooks/useMe.js";
import { useIsDesktop } from "../../hooks/useIsDesktop.js";
import { formatDateForInput } from "../../utils/users.js";
import { apiFetch, apiJson } from "../../utils/api.js";

/** ISO или "YYYY-MM-DDTHH:mm" → локальное "YYYY-MM-DDTHH:mm" для datetime-local. */
function dueAtToLocal(value) {
  if (value == null || value === "") return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${h}:${min}`;
}

/** YYYY-MM → первый день месяца YYYY-MM-01 */
function monthFirstDay(ym) {
  if (!ym || ym.length < 7) return "";
  return `${ym}-01`;
}
/** YYYY-MM → последний день месяца */
function monthLastDay(ym) {
  if (!ym || ym.length < 7) return "";
  const [y, m] = ym.split("-").map(Number);
  const last = new Date(y, m, 0);
  const day = String(last.getDate()).padStart(2, "0");
  return `${ym}-${day}`;
}
/** YYYY-MM → предыдущий месяц YYYY-MM */
function prevMonth(ym) {
  if (!ym || ym.length < 7) return ym;
  const [y, m] = ym.split("-").map(Number);
  if (m === 1) return `${y - 1}-12`;
  return `${y}-${String(m - 1).padStart(2, "0")}`;
}
/** YYYY-MM → следующий месяц */
function nextMonth(ym) {
  if (!ym || ym.length < 7) return ym;
  const [y, m] = ym.split("-").map(Number);
  if (m === 12) return `${y + 1}-01`;
  return `${y}-${String(m + 1).padStart(2, "0")}`;
}
/** Сетка календаря на месяц: массив недель, каждая неделя — массив { day: number | null, dateStr: string | null } */
function buildMonthGrid(ym) {
  if (!ym || ym.length < 7) return [];
  const [y, m] = ym.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const daysInMonth = new Date(y, m, 0).getDate();
  const startWeekday = (first.getDay() + 6) % 7; // Пн = 0
  const totalCells = startWeekday + daysInMonth;
  const rows = Math.ceil(totalCells / 7);
  const grid = [];
  let day = 0;
  for (let r = 0; r < rows; r++) {
    const week = [];
    for (let c = 0; c < 7; c++) {
      const i = r * 7 + c;
      if (i < startWeekday || day >= daysInMonth) {
        week.push({ day: null, dateStr: null });
      } else {
        day++;
        const dateStr = `${ym}-${String(day).padStart(2, "0")}`;
        week.push({ day, dateStr });
      }
    }
    grid.push(week);
  }
  return grid;
}

export function TasksPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin, me } = useMe();
  const isDesktop = useIsDesktop();

  const filtersOpen = useMemo(
    () => searchParams.get("filters") === "1",
    [searchParams]
  );
  const setFiltersOpen = useCallback(
    (open) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (open) p.set("filters", "1");
          else p.delete("filters");
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const todayStr = useMemo(() => formatDateForInput(new Date()), [me?.iana_timezone]);
  const dateFrom = useMemo(() => {
    const d = searchParams.get("date_from");
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
    return todayStr;
  }, [searchParams, todayStr]);
  const dateTo = useMemo(() => {
    const d = searchParams.get("date_to");
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
    return todayStr;
  }, [searchParams, todayStr]);
  const setDateRange = useCallback(
    (from, to) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (from) p.set("date_from", from);
          else p.delete("date_from");
          if (to) p.set("date_to", to);
          else p.delete("date_to");
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const search = useMemo(() => searchParams.get("search") ?? "", [searchParams]);
  const setSearch = useCallback(
    (v) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (v && v.trim()) p.set("search", v.trim());
          else p.delete("search");
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const managerId = useMemo(() => {
    const m = searchParams.get("manager");
    if (m === "" || m === null) return "";
    const n = parseInt(m, 10);
    return Number.isNaN(n) ? "" : n;
  }, [searchParams]);
  const setManagerId = useCallback(
    (v) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (v !== "" && v != null) p.set("manager", String(v));
          else p.delete("manager");
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [managers, setManagers] = useState([]);
  const [markingDone, setMarkingDone] = useState(false);
  const [taskDueAt, setTaskDueAt] = useState("");
  const [savingDue, setSavingDue] = useState(false);
  const lastDueAtRef = useRef("");
  const [modalTask, setModalTask] = useState(null);

  const taskIdParam = searchParams.get("task") ?? "";
  const dealIdParam = searchParams.get("deal") ?? "";
  const groups = data?.groups ?? [];
  const noDueGroups = data?.noDueGroups ?? [];
  const calendarMonth = useMemo(() => {
    if (dateFrom && /^\d{4}-\d{2}-\d{2}$/.test(dateFrom)) return dateFrom.slice(0, 7);
    return todayStr.slice(0, 7);
  }, [dateFrom, todayStr]);
  const tasksByDay = useMemo(() => {
    const map = new Map();
    for (const g of groups) {
      for (const t of g.tasks || []) {
        if (t.dueAt) {
          const key = t.dueAt.slice(0, 10);
          if (!map.has(key)) map.set(key, []);
          map.get(key).push({ ...t, managerName: g.managerName });
        }
      }
    }
    return map;
  }, [groups]);
  const noDueTasks = useMemo(
    () =>
      noDueGroups.flatMap((g) =>
        g.tasks.map((t) => ({ ...t, managerName: g.managerName }))
      ),
    [noDueGroups]
  );
  const hasAnyTasks = noDueTasks.length > 0 || groups.length > 0;
  const selectedTask = useMemo(() => {
    if (!taskIdParam || !dealIdParam || !data) return null;
    const tid = parseInt(taskIdParam, 10);
    const did = parseInt(dealIdParam, 10);
    if (Number.isNaN(tid) || Number.isNaN(did)) return null;
    for (const group of data.groups ?? []) {
      const t = group.tasks.find((x) => x.id === tid && x.dealId === did);
      if (t) return { ...t, managerName: group.managerName };
    }
    for (const group of data.noDueGroups ?? []) {
      const t = group.tasks.find((x) => x.id === tid && x.dealId === did);
      if (t) return { ...t, managerName: group.managerName };
    }
    return null;
  }, [taskIdParam, dealIdParam, data]);

  const displayTask = modalTask ?? selectedTask;

  useEffect(() => {
    if (!taskIdParam || !dealIdParam) {
      setModalTask(null);
      return;
    }
    if (selectedTask) {
      setModalTask((prev) => {
        if (!prev || prev.id !== selectedTask.id || prev.dealId !== selectedTask.dealId)
          return selectedTask;
        return prev;
      });
    }
  }, [taskIdParam, dealIdParam, selectedTask]);

  useEffect(() => {
    if (displayTask) {
      if (displayTask.dueAt != null && displayTask.dueAt !== "") {
        const v = dueAtToLocal(displayTask.dueAt);
        setTaskDueAt(v);
        lastDueAtRef.current = v;
      } else if (displayTask.time && dateFrom) {
        const v = `${dateFrom}T${displayTask.time}`;
        setTaskDueAt(v);
        lastDueAtRef.current = v;
      } else {
        setTaskDueAt("");
        lastDueAtRef.current = "";
      }
    } else {
      setTaskDueAt("");
      lastDueAtRef.current = "";
    }
  }, [displayTask, dateFrom]);

  const setTaskModal = useCallback(
    (taskId, dealId) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (taskId != null && dealId != null) {
            p.set("task", String(taskId));
            p.set("deal", String(dealId));
          } else {
            p.delete("task");
            p.delete("deal");
          }
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const goToPrevMonth = useCallback(() => {
    const prev = prevMonth(calendarMonth);
    setDateRange(monthFirstDay(prev), monthLastDay(prev));
  }, [calendarMonth, setDateRange]);
  const goToNextMonth = useCallback(() => {
    const next = nextMonth(calendarMonth);
    setDateRange(monthFirstDay(next), monthLastDay(next));
  }, [calendarMonth, setDateRange]);

  const scope = isAdmin ? "all" : "mine";

  const loadManagers = useCallback(async () => {
    try {
      const res = await apiJson("/api/managers");
      setManagers(res?.items ?? []);
    } catch {
      setManagers([]);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) loadManagers();
  }, [isAdmin, loadManagers]);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      params.set("scope", scope);
      if (search.trim()) params.set("search", search.trim());
      if (isAdmin && managerId !== "") params.set("manager_id", String(managerId));
      const res = await apiJson(`/api/tasks?${params.toString()}`);
      setData(res);
    } catch (e) {
      setError(e.message || "Ошибка загрузки");
      setData({ groups: [], noDueGroups: [] });
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, scope, search, isAdmin, managerId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  /** Завершение задачи: при требовании договора/квалификации — переход на сделку с нужной модалкой (как в карточке сделки). */
  const markTaskDoneFromModal = useCallback(async () => {
    if (!displayTask) return;
    setMarkingDone(true);
    try {
      const res = await apiFetch(
        `/api/deals/${displayTask.dealId}/tasks/${displayTask.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ done: true }),
        }
      );
      if (res.ok) {
        setTaskModal(null, null);
        loadTasks();
        return;
      }
      const data = await res.json().catch(() => ({}));
      const detail = data?.detail;
      if (res.status === 400 && detail && typeof detail === "object") {
        if (detail.require_action === "contract_upload") {
          setTaskModal(null, null);
          navigate(`/deals/${displayTask.dealId}?modal=contractUpload`);
          return;
        }
        if (detail.require_action === "qualification") {
          setTaskModal(null, null);
          navigate(`/deals/${displayTask.dealId}?modal=qualification`);
          return;
        }
      }
      const msg =
        typeof detail === "string"
          ? detail
          : detail?.message || JSON.stringify(detail || data || "Ошибка");
      window.alert(msg);
    } catch (e) {
      window.alert(e?.message || "Ошибка");
    } finally {
      setMarkingDone(false);
    }
  }, [displayTask, loadTasks, navigate, setTaskModal]);

  useEffect(() => {
    if (!isDesktop) return;
    if (dateFrom && dateTo && dateFrom === dateTo) {
      const ym = dateFrom.slice(0, 7);
      setDateRange(monthFirstDay(ym), monthLastDay(ym));
    }
  }, [isDesktop, dateFrom, dateTo, setDateRange]);

  const managerOptions = useMemo(() => {
    const list = [
      { value: "", label: "Все менеджеры" },
      ...managers.map((m) => ({
        value: m.id,
        label: m.display_name || m.code || `id=${m.id}`,
      })),
    ];
    return list;
  }, [managers]);

  const hasActiveFilters = !!search.trim() || (isAdmin && managerId !== "");
  const managerLabel =
    managerId !== ""
      ? managers.find((m) => m.id === managerId)?.display_name ||
        managers.find((m) => m.id === managerId)?.code ||
        `id=${managerId}`
      : "";
  const filterChips = [
    search.trim() && {
      key: "search",
      label: `Поиск: ${search.trim()}`,
      onRemove: () => setSearch(""),
    },
    isAdmin &&
      managerId !== "" && {
        key: "manager",
        label: `Менеджер: ${managerLabel}`,
        onRemove: () => setManagerId(""),
      },
  ].filter(Boolean);
  const onResetFilters = () => {
    setSearch("");
    if (isAdmin) setManagerId("");
    setDateRange(todayStr, todayStr);
  };

  const dateRangeLabel =
    dateFrom === dateTo
      ? new Date(dateFrom + "T12:00:00").toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" })
      : `${new Date(dateFrom + "T12:00:00").toLocaleDateString("ru-RU", { day: "numeric", month: "short" })} — ${new Date(dateTo + "T12:00:00").toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" })}`;

  const FilterIcon = (
    <svg className="crmIconSvg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 6h16" />
      <path d="M7 12h10" />
      <path d="M10 18h4" />
    </svg>
  );

  if (loading && !data) {
    return <LoadingBlock />;
  }

  return (
    <>
      <main className="chat" id="tasksScreen">
        <div className="crmStickyStack">
          <div className="crmToolbar">
            <div className="crmToolbarRow">
              <input
                type="search"
                className="input crmSearchInput"
                placeholder="Поиск по названию задачи"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Поиск по названию"
              />
              <button
                type="button"
                className={`crmIconBtn ${hasActiveFilters ? "isActive" : ""}`}
                title="Фильтры"
                aria-label="Фильтры"
                onClick={() => setFiltersOpen(true)}
              >
                {FilterIcon}
              </button>
            </div>
            {hasActiveFilters ? (
              <FilterChips chips={filterChips} onReset={onResetFilters} />
            ) : null}
          </div>
        </div>
        <div className="section" style={{ paddingTop: 8 }}>
          <div className="crmListSurface" id="list">
          {error && (
            <div className="sectionTitle" style={{ color: "var(--colorErr)" }}>
              {error}
            </div>
          )}
          {isDesktop ? (
            <div className="tasksCalendarWrap">
              <div className="tasksCalendarHeader">
                <button type="button" className="tasksCalendarMonthBtn" onClick={goToPrevMonth} aria-label="Предыдущий месяц">‹</button>
                <span className="tasksCalendarMonthTitle">
                  {new Date(calendarMonth + "-01").toLocaleDateString("ru-RU", { month: "long", year: "numeric" })}
                </span>
                <button type="button" className="tasksCalendarMonthBtn" onClick={goToNextMonth} aria-label="Следующий месяц">›</button>
              </div>
              {noDueTasks.length > 0 && (
                <div className="tasksCalendarNoDue">
                  <div className="tasksCalendarNoDueTitle">Задачи без срока</div>
                  <div className="tasksCalendarNoDueList">
                    {noDueTasks.map((t) => (
                      <CrmRow
                        key={`${t.dealId}-${t.id}`}
                        barClass="bad"
                        title={t.title}
                        right="Без срока"
                        bottom={<span className="crmMeta">Сделка: {t.dealTitle}</span>}
                        onClick={() => setTaskModal(t.id, t.dealId)}
                      />
                    ))}
                  </div>
                </div>
              )}
              <div className="tasksCalendarGridWrap">
                <table className="tasksCalendarTable" role="grid">
                  <thead>
                    <tr>
                      {["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"].map((d) => (
                        <th key={d} scope="col" className="tasksCalendarWeekday">{d}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {buildMonthGrid(calendarMonth).map((week, wi) => (
                      <tr key={wi}>
                        {week.map((cell, ci) => {
                          const tasks = cell.dateStr ? (tasksByDay.get(cell.dateStr) || []) : [];
                          const isToday = cell.dateStr === todayStr;
                          return (
                            <td key={ci} className={`tasksCalendarCell ${isToday ? "tasksCalendarCellToday" : ""}`}>
                              {cell.day != null ? (
                                <>
                                  <span className="tasksCalendarDayNum">{cell.day}</span>
                                  <div className="tasksCalendarDayTasks">
                                    {tasks.map((t) => (
                                      <button
                                        key={`${t.dealId}-${t.id}`}
                                        type="button"
                                        className="tasksCalendarTaskBtn"
                                        onClick={() => setTaskModal(t.id, t.dealId)}
                                        title={`${t.title} · ${t.time}`}
                                      >
                                        {t.title}
                                      </button>
                                    ))}
                                  </div>
                                </>
                              ) : null}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!hasAnyTasks && (
                <div className="sectionTitle" style={{ opacity: 0.8, marginTop: 12 }}>
                  Нет задач на этот месяц
                </div>
              )}
            </div>
          ) : !hasAnyTasks ? (
            <div>
              <div className="crmGroupHeader">
                Задачи на {dateRangeLabel}
              </div>
              <div className="sectionTitle" style={{ opacity: 0.8 }}>
                Нет задач
              </div>
            </div>
          ) : (
            <>
              {noDueTasks.length > 0 && (
                <div className="dealTasksBlock dealTasksBlockNoDue">
                  <div className="crmGroupHeader dealTasksBlockTitle">Без срока</div>
                  {noDueTasks.map((t) => (
                    <CrmRow
                      key={`${t.dealId}-${t.id}`}
                      barClass="bad"
                      title={t.title}
                      right="Без срока"
                      bottom={
                        <span className="crmMeta">
                          Сделка: {t.dealTitle}
                        </span>
                      }
                      onClick={() => setTaskModal(t.id, t.dealId)}
                    />
                  ))}
                </div>
              )}
              {groups.length > 0 && (
                <div className="dealTasksBlock" style={{ marginTop: noDueTasks.length > 0 ? 12 : 0 }}>
                  {noDueTasks.length > 0 && (
                    <div className="dealTasksBlockTitle">
                      На {dateRangeLabel}
                    </div>
                  )}
                  {groups.map((group) => (
                    <div key={group.managerName}>
                      <div className="crmGroupHeader">{group.managerName}</div>
                      {group.tasks.map((t) => (
                        <CrmRow
                          key={`${t.dealId}-${t.id}`}
                          barClass="good"
                          title={t.title}
                          right={t.time}
                          bottom={
                            <span className="crmMeta">
                              Сделка: {t.dealTitle}
                            </span>
                          }
                          onClick={() => setTaskModal(t.id, t.dealId)}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          </div>
        </div>
      </main>
      <button
        type="button"
        className="fab"
        title="Новая задача"
        aria-label="Новая задача"
      >
        +
      </button>

      <BottomSheetModal
        open={!!(taskIdParam && dealIdParam)}
        onClose={() => setTaskModal(null, null)}
        title="Задача"
      >
        {displayTask && (
          <div className="section">
            <div className="sectionTitle" style={{ marginBottom: 8, padding: 0 }}>
              {displayTask.title}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 12 }}>
              <label className="dealsFilterLabel" style={{ marginBottom: 0 }}>
                Срок выполнения
              </label>
              <input
                type="datetime-local"
                className="input"
                style={{ width: "100%", boxSizing: "border-box" }}
                value={taskDueAt}
                disabled={savingDue}
                onChange={async (e) => {
                  const val = e.target.value;
                  setTaskDueAt(val);
                  setSavingDue(true);
                  try {
                    await apiJson(
                      `/api/deals/${displayTask.dealId}/tasks/${displayTask.id}`,
                      {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          due_at: val ? new Date(val).toISOString() : null,
                        }),
                      }
                    );
                    lastDueAtRef.current = val;
                    setModalTask((prev) =>
                      prev ? { ...prev, time: val ? new Date(val).toISOString().slice(11, 16) : null, dueAt: val || null } : null
                    );
                    loadTasks();
                  } catch (err) {
                    setTaskDueAt(lastDueAtRef.current);
                    window.alert(err.message || "Ошибка сохранения");
                  } finally {
                    setSavingDue(false);
                  }
                }}
                aria-label="Срок выполнения"
              />
            </div>
            <div className="crmMeta" style={{ marginBottom: 4 }}>
              {displayTask.managerName}
            </div>
            <p className="crmMeta" style={{ marginBottom: 12 }}>
              Сделка: {displayTask.dealTitle}
            </p>
            {displayTask.description?.trim() ? (
              <p className="crmMeta" style={{ marginBottom: 12, whiteSpace: "pre-wrap" }}>
                {displayTask.description.trim()}
              </p>
            ) : null}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button
                type="button"
                className="crmIconBtn"
                style={{ width: "100%", justifyContent: "center" }}
                onClick={() => navigate(`/deals/${displayTask.dealId}`)}
              >
                Открыть сделку
              </button>
              <button
                type="button"
                className="linkBtn"
                style={{ width: "100%", justifyContent: "center" }}
                onClick={markTaskDoneFromModal}
                disabled={markingDone}
              >
                {markingDone ? "…" : "Выполнено"}
              </button>
            </div>
          </div>
        )}
      </BottomSheetModal>

      <FiltersModal open={filtersOpen} onClose={() => setFiltersOpen(false)}>
        <div className="dealsFiltersBody">
          <div className="dealsFilterRow">
            <label className="dealsFilterLabel">Период</label>
            <div className="dealsFilterDateRow">
              <input
                type="date"
                className="input dealsFilterSelect dealsFilterDate"
                value={dateFrom}
                onChange={(e) => setDateRange(e.target.value, dateTo)}
                aria-label="С"
              />
              <span className="dealsFilterDateSep">—</span>
              <input
                type="date"
                className="input dealsFilterSelect dealsFilterDate"
                value={dateTo}
                onChange={(e) => setDateRange(dateFrom, e.target.value)}
                aria-label="По"
              />
            </div>
            <button
              type="button"
              className="chip"
              style={{ marginTop: 6 }}
              onClick={() => setDateRange(todayStr, todayStr)}
              title="Сегодня"
            >
              Сегодня
            </button>
          </div>
          {isAdmin && (
            <div className="dealsFilterRow">
              <label className="dealsFilterLabel">Менеджер</label>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CustomSelect
                  className="input dealsFilterSelect"
                  options={managerOptions}
                  value={managerId}
                  onChange={setManagerId}
                  placeholder="Все менеджеры"
                  searchable
                />
              </div>
            </div>
          )}
          <div className="dealsFilterActions">
            <button
              type="button"
              className="linkBtn"
              style={{ flex: 1, justifyContent: "center" }}
              onClick={() => setFiltersOpen(false)}
            >
              Применить
            </button>
            <button
              type="button"
              className="crmIconBtn"
              title="Сбросить"
              onClick={onResetFilters}
            >
              ↺
            </button>
          </div>
        </div>
      </FiltersModal>
    </>
  );
}
